import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, CaretUp, CaretDown, Sparkle, TextH, TextT, Image as ImageIcon, CursorClick, Eye, X, ArrowRight } from "@phosphor-icons/react";

const BLOCK_TEMPLATES = {
  heading: { type: "heading", value: "Awesome offer" },
  text:    { type: "text",    value: "Explain the offer in a paragraph." },
  image:   { type: "image",   image_url: "https://placehold.co/800x300", link_url: "", label: "" },
  button:  { type: "button",  label: "Yes I want this deal before it ends!", link_url: "https://example.com" },
};

function reorder(list, from, to) {
  const arr = [...list];
  const [moved] = arr.splice(from, 1);
  arr.splice(to, 0, moved);
  return arr;
}

export default function AdminSurfDeals() {
  const [deals, setDeals] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [previewDeal, setPreviewDeal] = useState(null);

  const current = deals.find((d) => d.id === activeId);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/surf-deals");
      setDeals(data.deals || []);
      setFeatureEnabled(data.enabled !== false);
      if (!activeId && data.deals?.length) setActiveId(data.deals[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const toggleFeature = async () => {
    setToggling(true);
    try {
      const next = !featureEnabled;
      const { data } = await api.patch("/admin/surf-deals-feature", { enabled: next });
      setFeatureEnabled(!!data.enabled);
      toast.success(data.enabled ? "Surf deals feature is ON" : "Surf deals feature is OFF");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setToggling(false); }
  };

  const createDeal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/surf-deals", { title: "New deal", blocks: [] });
      await load();
      setActiveId(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const patchCurrent = (patch) => {
    setDeals((rows) => rows.map((d) => (d.id === activeId ? { ...d, ...patch } : d)));
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await api.patch(`/admin/surf-deals/${current.id}`, {
        title: current.title,
        blocks: current.blocks || [],
      });
      toast.success("Deal saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    setBusy(true);
    try {
      await api.patch(`/admin/surf-deals/${d.id}`, { title: d.title, blocks: d.blocks || [], active: !d.active });
      toast.success(d.active ? "Deal disabled" : "Deal is now live");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeDeal = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try {
      await api.delete(`/admin/surf-deals/${d.id}`);
      toast.success("Deleted");
      if (activeId === d.id) setActiveId("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addBlock = (type) => patchCurrent({ blocks: [...(current.blocks || []), { ...BLOCK_TEMPLATES[type] }] });
  const removeBlock = (i) => patchCurrent({ blocks: (current.blocks || []).filter((_, idx) => idx !== i) });
  const moveBlock = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= (current.blocks || []).length) return;
    patchCurrent({ blocks: reorder(current.blocks, i, to) });
  };
  const editBlock = (i, patch) => {
    const arr = [...(current.blocks || [])];
    arr[i] = { ...arr[i], ...patch };
    patchCurrent({ blocks: arr });
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Surf deals
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Build a sales page shown right before a user starts a surf session.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="surf-deal-preview-current"
            onClick={() => current ? setPreviewDeal(current) : toast.info("Pick or create a deal first")}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm font-bold px-4 py-2"
          >
            <Eye size={14} weight="bold" /> Preview
          </button>
          <button
            data-testid="surf-deal-create"
            onClick={createDeal}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-4 py-2"
          >
            <Plus size={14} weight="bold" /> New deal
          </button>
        </div>
      </div>

      {/* Master feature switch — flips the entire login-deals redirect on or off. */}
      <div
        data-testid="surf-deals-feature-card"
        className={`mb-5 rounded-2xl border p-4 flex items-center justify-between gap-4 flex-wrap ${
          featureEnabled
            ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30"
            : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
        }`}
      >
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Feature status</div>
          <div className="mt-0.5 font-black text-slate-900 dark:text-white">
            {featureEnabled ? "Surf deals are ON" : "Surf deals are OFF"}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-lg">
            {featureEnabled
              ? "Users clicking Start Surfing will see the currently-active deal before the surf session."
              : "Users will skip the promo screen entirely and go straight to the surf session."}
          </div>
        </div>
        <button
          data-testid="surf-deals-feature-toggle"
          role="switch"
          aria-checked={featureEnabled}
          aria-label="Toggle surf deals feature"
          onClick={toggleFeature}
          disabled={toggling}
          className={`relative inline-flex h-8 w-16 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
            featureEnabled ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
          }`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
              featureEnabled ? "translate-x-9" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        <aside className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 space-y-2 h-fit">
          {deals.length === 0 && <div className="text-xs text-slate-500 p-4 text-center">No deals yet. Create one to get started.</div>}
          {deals.map((d) => (
            <div
              key={d.id}
              onClick={() => setActiveId(d.id)}
              className={`rounded-xl px-3 py-2 cursor-pointer ${activeId === d.id ? "bg-blue-50 dark:bg-blue-500/15 border border-blue-300 dark:border-blue-500/40" : "hover:bg-slate-50 dark:hover:bg-slate-800 border border-transparent"}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-black text-sm text-slate-900 dark:text-white truncate">{d.title}</span>
                {d.active && <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300 rounded-full px-1.5 py-0.5"><Sparkle size={9} weight="fill" /> Live</span>}
              </div>
              <div className="flex justify-between items-center mt-1 text-[10px] uppercase tracking-widest text-slate-500">
                <span>{(d.blocks || []).length} blocks</span>
                <div className="flex gap-1">
                  <button data-testid={`surf-deal-preview-${d.id}`} onClick={(e) => { e.stopPropagation(); setPreviewDeal(d); }} className="hover:text-blue-600">Preview</button>
                  <span>·</span>
                  <button data-testid={`surf-deal-toggle-${d.id}`} onClick={(e) => { e.stopPropagation(); toggleActive(d); }} className="hover:text-blue-600">{d.active ? "Disable" : "Activate"}</button>
                  <span>·</span>
                  <button onClick={(e) => { e.stopPropagation(); removeDeal(d); }} className="hover:text-rose-600">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </aside>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          {!current ? (
            <div className="text-slate-500 text-sm py-10 text-center">Pick or create a deal on the left.</div>
          ) : (
            <>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Title</label>
              <input
                data-testid="surf-deal-title"
                value={current.title}
                onChange={(e) => patchCurrent({ title: e.target.value })}
                className="w-full mt-1 mb-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
              />

              <div className="flex flex-wrap gap-2 mb-4">
                <button data-testid="add-block-heading" onClick={() => addBlock("heading")} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-xs font-bold"><TextH size={12} weight="bold" /> Add heading</button>
                <button data-testid="add-block-text"    onClick={() => addBlock("text")}    className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-xs font-bold"><TextT size={12} weight="bold" /> Add text</button>
                <button data-testid="add-block-image"   onClick={() => addBlock("image")}   className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-xs font-bold"><ImageIcon size={12} weight="bold" /> Add image</button>
                <button data-testid="add-block-button"  onClick={() => addBlock("button")}  className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-xs font-bold"><CursorClick size={12} weight="bold" /> Add button</button>
              </div>

              <div className="space-y-3">
                {(current.blocks || []).map((b, i) => (
                  <div key={i} data-testid={`block-${i}`} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{b.type}</div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => moveBlock(i, -1)} className="rounded-full w-7 h-7 inline-flex items-center justify-center bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"><CaretUp size={12} weight="bold" /></button>
                        <button onClick={() => moveBlock(i, +1)} className="rounded-full w-7 h-7 inline-flex items-center justify-center bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700"><CaretDown size={12} weight="bold" /></button>
                        <button onClick={() => removeBlock(i)} className="rounded-full w-7 h-7 inline-flex items-center justify-center bg-white dark:bg-slate-900 hover:bg-rose-50 dark:hover:bg-rose-500/10 border border-slate-200 dark:border-slate-700 text-rose-600"><Trash size={12} weight="bold" /></button>
                      </div>
                    </div>

                    {(b.type === "heading" || b.type === "text") && (
                      b.type === "text" ? (
                        <textarea
                          rows={3}
                          value={b.value || ""}
                          onChange={(e) => editBlock(i, { value: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                        />
                      ) : (
                        <input
                          value={b.value || ""}
                          onChange={(e) => editBlock(i, { value: e.target.value })}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                        />
                      )
                    )}
                    {b.type === "image" && (
                      <div className="grid md:grid-cols-2 gap-2">
                        <div className="md:col-span-2 flex flex-col sm:flex-row gap-2">
                          <input
                            placeholder="Image URL (or upload below)"
                            value={b.image_url || ""}
                            onChange={(e) => editBlock(i, { image_url: e.target.value })}
                            className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                          />
                          <label
                            data-testid={`block-image-upload-${i}`}
                            className="inline-flex items-center justify-center gap-1 cursor-pointer rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold px-3 py-2 whitespace-nowrap"
                          >
                            <ImageIcon size={12} weight="bold" /> Upload image
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const form = new FormData();
                                form.append("file", file);
                                try {
                                  const { data } = await api.post("/uploads/post-image", form, {
                                    headers: { "Content-Type": "multipart/form-data" },
                                  });
                                  editBlock(i, { image_url: data.url });
                                  toast.success("Image uploaded");
                                } catch (err) {
                                  toast.error(formatApiError(err));
                                } finally {
                                  e.target.value = "";
                                }
                              }}
                            />
                          </label>
                        </div>
                        <input placeholder="Optional link URL" value={b.link_url || ""} onChange={(e) => editBlock(i, { link_url: e.target.value })} className="md:col-span-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                        {b.image_url && <img src={b.image_url} alt="preview" className="md:col-span-2 rounded-lg max-h-40 object-contain bg-white" />}
                      </div>
                    )}
                    {b.type === "button" && (
                      <div className="grid md:grid-cols-2 gap-2">
                        <input placeholder="Label" value={b.label || ""} onChange={(e) => editBlock(i, { label: e.target.value })} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                        <input placeholder="Link URL" value={b.link_url || ""} onChange={(e) => editBlock(i, { link_url: e.target.value })} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-5 flex justify-end">
                <button
                  data-testid="login-deal-save"
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-5 py-2.5 disabled:opacity-60"
                >
                  <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {previewDeal && (
        <div
          data-testid="surf-deal-preview-modal"
          onClick={() => setPreviewDeal(null)}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto py-10 px-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-3xl mx-auto rounded-3xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-2xl overflow-hidden"
          >
            <div className="px-6 py-4 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">Special offer</span>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">· Admin preview</span>
              </div>
              <button
                data-testid="surf-deal-preview-close"
                onClick={() => setPreviewDeal(null)}
                aria-label="Close preview"
                className="rounded-full w-9 h-9 inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
              >
                <X size={14} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                {previewDeal.title}
              </h1>
              {(previewDeal.blocks || []).length === 0 && (
                <div className="text-sm text-slate-500 italic">This deal has no blocks yet — add some heading, text, image or button blocks to see them here.</div>
              )}
              {(previewDeal.blocks || []).map((b, i) => <PreviewBlock key={i} block={b} />)}
            </div>

            <div className="px-6 py-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex flex-col items-center gap-3">
              <div className="text-sm text-slate-500">Not interested right now?</div>
              <button
                onClick={() => setPreviewDeal(null)}
                className="w-full max-w-md inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-black text-white text-xl md:text-2xl font-black uppercase tracking-widest px-8 py-5 md:py-6 shadow-lg"
              >
                Skip offer <ArrowRight size={22} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function PreviewBlock({ block }) {
  if (!block) return null;
  const { type, value, image_url, link_url, label } = block;
  if (type === "heading") {
    return <h2 className="text-xl md:text-2xl font-black tracking-tight text-slate-900 dark:text-white mt-2">{value}</h2>;
  }
  if (type === "text") {
    return <p className="text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{value}</p>;
  }
  if (type === "image") {
    const img = <img src={image_url || value} alt={label || ""} className="w-full rounded-2xl border border-slate-200 dark:border-slate-800" />;
    return link_url ? <a href={link_url} target="_blank" rel="noreferrer noopener" className="block">{img}</a> : img;
  }
  if (type === "button") {
    return (
      <a
        href={link_url || value || "#"}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1 rounded-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-black uppercase tracking-widest text-sm px-5 py-2.5 mt-2"
      >
        {label || value || "Yes I want this deal before it ends!"} <ArrowRight size={14} weight="bold" />
      </a>
    );
  }
  return null;
}
